<template>
  <div class="portfolio-screen">
    
  <div class="mb-4">
      <v-btn 
        variant="text" 
        prepend-icon="mdi-arrow-left" 
        color="grey-darken-2" 
        class="px-0" 
        :ripple="false"
        @click="goBack"
      >
        Back to Mutual Funds
      </v-btn>
    </div>

    <header class="portfolio-header">
      <div class="title-container">
        <h1>Retirement Portfolio</h1>
      </div>
      <div class="value-container">
        <p class="value-label">Total Portfolio Value</p>
        <p class="value-display">{{ totalPortfolioValueFormatted }}</p>
        <button class="action-btn refresh-nav-btn" @click="fetchPortfolio" :disabled="loading">
          <span>{{ loading ? '⏳' : '🔄' }}</span> Refresh Latest NAV
        </button>
      </div>
    </header>

    <section class="bucket-dashboard">
      <h2 class="dashboard-title">Portfolio Balance Allocation</h2>
      
      <div v-if="loading" class="loading-state">Calculating allocation...</div>
      
      <div v-else class="bucket-visualizer">
        <div 
          v-for="(bucket, index) in bucketSummaries" 
          :key="bucket.id" 
          class="bucket-wrapper"
        >
          <div class="bucket-info">
            <h3 :style="{ color: bucket.color }">{{ bucket.title }}</h3>
            <p class="bucket-value">{{ formatINR(bucket.total) }}</p>
            <p class="bucket-percent">{{ bucket.percent.toFixed(1) }}%</p>
          </div>

          <div class="bucket-graphic">
            <div class="bucket-body">
              <div 
                class="bucket-fill" 
                :style="{ 
                  height: fillHeights[index] + '%', 
                  backgroundColor: bucket.color 
                }"
              ></div>
            </div>
            <div class="bucket-base"></div>
          </div>
        </div>
      </div>
    </section>

    <main class="portfolio-buckets">
      
      <section class="bucket-section bucket-1">
        <header class="bucket-header spend-now">
          <h2>SPEND NOW / Your Safety Net (Bucket 1)</h2>
          <p class="bucket-description">Designed for immediate accessibility and minimal risk.</p>
        </header>
        <div class="fund-table-container">
          <div v-if="loading" class="loading-state">Loading Bucket 1 funds...</div>
          <table v-else class="fund-table">
             <thead>
              <tr>
                <th rowspan="2">Account Name</th>
                <th rowspan="2">MF Name</th>
                <th rowspan="2">Fund Type</th>
                <th rowspan="2">Units</th>
                <th rowspan="2">Current NAV (₹)</th>
                <th rowspan="2">Current Value (₹)</th>
                <th colspan="5" class="performance-group-header">Fund Performance</th>
              </tr>
              <tr>
                <th>YTD</th>
                <th>1Y</th>
                <th>3Y</th>
                <th>5Y</th>
                <th>Max</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fund in bucket1Funds" :key="fund.isin || fund._id">
                <td>{{ fund.accountName || 'N/A' }}</td>
                <td>{{ fund.schemeName }}</td>
                <td>{{ fund.fundCategory }}</td>
                <td>{{ fund.quantity }}</td>
                <td>{{ formatINR(fund.currentNav) }}</td>
                <td>{{ formatINR(fund.currentValue) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.ytd)">{{ formatReturn(fund.mfReturns?.ytd) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.one)">{{ formatReturn(fund.mfReturns?.one) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.three)">{{ formatReturn(fund.mfReturns?.three) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.five)">{{ formatReturn(fund.mfReturns?.five) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.max)">{{ formatReturn(fund.mfReturns?.max) }}</td>
              </tr>
              <tr v-if="bucket1Funds.length === 0">
                <td colspan="11" style="text-align: center; color: #6c757d;">No funds assigned to Bucket 1.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bucket-section bucket-2">
        <header class="bucket-header save-soon">
          <h2>SAVE SOON / Your Short-to-Mid-Term Goals (Bucket 2)</h2>
          <p class="bucket-description">A balanced mix of stability and growth.</p>
        </header>
        <div class="fund-table-container">
          <div v-if="loading" class="loading-state">Loading Bucket 2 funds...</div>
          <table v-else class="fund-table">
             <thead>
              <tr>
                <th rowspan="2">Account Name</th>
                <th rowspan="2">MF Name</th>
                <th rowspan="2">Fund Type</th>
                <th rowspan="2">Units</th>
                <th rowspan="2">Current NAV (₹)</th>
                <th rowspan="2">Current Value (₹)</th>
                <th colspan="5" class="performance-group-header">Fund Performance</th>
              </tr>
              <tr>
                <th>YTD</th>
                <th>1Y</th>
                <th>3Y</th>
                <th>5Y</th>
                <th>Max</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fund in bucket2Funds" :key="fund.isin || fund._id">
                <td>{{ fund.accountName || 'N/A' }}</td>
                <td>{{ fund.schemeName }}</td>
                <td>{{ fund.fundCategory }}</td>
                <td>{{ fund.quantity }}</td>
                <td>{{ formatINR(fund.currentNav) }}</td>
                <td>{{ formatINR(fund.currentValue) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.ytd)">{{ formatReturn(fund.mfReturns?.ytd) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.one)">{{ formatReturn(fund.mfReturns?.one) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.three)">{{ formatReturn(fund.mfReturns?.three) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.five)">{{ formatReturn(fund.mfReturns?.five) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.max)">{{ formatReturn(fund.mfReturns?.max) }}</td>
              </tr>
              <tr v-if="bucket2Funds.length === 0">
                <td colspan="11" style="text-align: center; color: #6c757d;">No funds assigned to Bucket 2.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bucket-section bucket-3">
        <header class="bucket-header secure-later">
          <h2>SECURE LATER / Your Long-Term Wealth (Bucket 3)</h2>
          <p class="bucket-description">Focus on maximum growth for distant goals, accepting higher volatility.</p>
        </header>
        <div class="fund-table-container">
          <div v-if="loading" class="loading-state">Loading Bucket 3 funds...</div>
          <table v-else class="fund-table">
           <thead>
              <tr>
                <th rowspan="2">Account Name</th>
                <th rowspan="2">MF Name</th>
                <th rowspan="2">Fund Type</th>
                <th rowspan="2">Units</th>
                <th rowspan="2">Current NAV (₹)</th>
                <th rowspan="2">Current Value (₹)</th>
                <th colspan="5" class="performance-group-header">Fund Performance</th>
              </tr>
              <tr>
                <th>YTD</th>
                <th>1Y</th>
                <th>3Y</th>
                <th>5Y</th>
                <th>Max</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fund in bucket3Funds" :key="fund.isin || fund._id">
                <td>{{ fund.accountName || 'N/A' }}</td>
                <td>{{ fund.schemeName }}</td>
                <td>{{ fund.fundCategory }}</td>
                <td>{{ fund.quantity }}</td>
                <td>{{ formatINR(fund.currentNav) }}</td>
                <td>{{ formatINR(fund.currentValue) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.ytd)">{{ formatReturn(fund.mfReturns?.ytd) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.one)">{{ formatReturn(fund.mfReturns?.one) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.three)">{{ formatReturn(fund.mfReturns?.three) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.five)">{{ formatReturn(fund.mfReturns?.five) }}</td>
                <td :class="getReturnClass(fund.mfReturns?.max)">{{ formatReturn(fund.mfReturns?.max) }}</td>
              </tr>
              <tr v-if="bucket3Funds.length === 0">
                <td colspan="11" style="text-align: center; color: #6c757d;">No funds assigned to Bucket 3.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

const router = useRouter()

const goBack = () => {
  router.push('/mf') 
}

const store = useStore()

const loading = ref(false)
const appLogoUrl = ref('https://cdn.icon-icons.com/icons2/2389/PNG/512/investment_portfolio_icon_145330.png')
const portfolioHoldings = ref([])

// State for the animation heights (starts at 0)
const fillHeights = ref([0, 0, 0])

const fetchPortfolio = async () => {
  loading.value = true
  // Reset animation heights before fetching
  fillHeights.value = [0, 0, 0] 
  
  try {
    const res = await store.dispatch('portfolio/getMfPortfolio')
    if (res && res.mfPortoflioList) {
      portfolioHoldings.value = res.mfPortoflioList.map(item => ({
        ...item,
        currentNav: item.nav,
        currentValue: item.quantity * item.nav,
        accountName: item.accountName,
        fundCategory: item.fundCategory 
      }))
    }
  } catch (err) {
    console.error("Failed to fetch portfolio", err)
  } finally {
    loading.value = false
  }
}

const formatINR = (num) => {
  return new Intl.NumberFormat('en-IN', { 
    style: 'currency', 
    currency: 'INR', 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 0 
  }).format(num || 0)
}

// Return formatting helpers
const formatReturn = (val) => {
  if (val === undefined || val === null || val === 0) return '-'
  return `${val > 0 ? '+' : ''}${val.toFixed(2)}%`
}

const getReturnClass = (val) => {
  if (!val || val === 0) return 'text-muted'
  return val > 0 ? 'text-green' : 'text-red'
}

// Data Computations
const totalPortfolioValue = computed(() => {
  return portfolioHoldings.value.reduce((sum, item) => sum + (item.currentValue || 0), 0)
})

const totalPortfolioValueFormatted = computed(() => {
  return formatINR(totalPortfolioValue.value)
})

const bucket1Funds = computed(() => portfolioHoldings.value.filter(fund => fund.bucketType === 'BUCKET1'))
const bucket2Funds = computed(() => portfolioHoldings.value.filter(fund => fund.bucketType === 'BUCKET2'))
const bucket3Funds = computed(() => portfolioHoldings.value.filter(fund => fund.bucketType === 'BUCKET3'))

// Calculate aggregate summaries for the animation
const bucketSummaries = computed(() => {
  const t1 = bucket1Funds.value.reduce((sum, f) => sum + (f.currentValue || 0), 0)
  const t2 = bucket2Funds.value.reduce((sum, f) => sum + (f.currentValue || 0), 0)
  const t3 = bucket3Funds.value.reduce((sum, f) => sum + (f.currentValue || 0), 0)
  
  // Prevent division by zero if portfolio is empty
  const total = totalPortfolioValue.value > 0 ? totalPortfolioValue.value : 1 

  return [
    { id: 1, title: 'Bucket 1 (Liquidity)', total: t1, percent: (t1 / total) * 100, color: '#007bff' },
    { id: 2, title: 'Bucket 2 (Stability)', total: t2, percent: (t2 / total) * 100, color: '#fd7e14' },
    { id: 3, title: 'Bucket 3 (Growth)', total: t3, percent: (t3 / total) * 100, color: '#28a745' }
  ]
})

// Trigger animation once data is loaded
watch(bucketSummaries, (newSummaries) => {
  if (totalPortfolioValue.value > 0 && !loading.value) {
    // Slight delay to allow the DOM to render the empty buckets first
    setTimeout(() => {
      fillHeights.value = newSummaries.map(s => s.percent)
    }, 100)
  }
}, { deep: true })

onMounted(() => {
  fetchPortfolio()
})
</script>

<style scoped>
.portfolio-screen {
  padding: 2rem;
  background-color: #f8f9fa;
  font-family: Arial, sans-serif;
}

/* Header Styling */
.portfolio-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.title-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.app-logo {
  height: 40px;
}

.value-container {
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.value-label {
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
}

.value-display {
  font-size: 2rem;
  font-weight: bold;
  color: #28a745;
  margin: 0;
}

.refresh-nav-btn {
  background-color: #007bff;
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.refresh-nav-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* --- ANIMATED BUCKET DASHBOARD STYLES --- */
.bucket-dashboard {
  background-color: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  margin: 0 0 2rem 0;
  color: #343a40;
  font-size: 1.5rem;
}

.bucket-visualizer {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 2rem;
}

.bucket-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 200px;
}

.bucket-info {
  margin-bottom: 1rem;
}

.bucket-info h3 {
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
}

.bucket-value {
  font-weight: bold;
  font-size: 1.2rem;
  color: #495057;
  margin: 0;
}

.bucket-percent {
  color: #6c757d;
  margin: 0;
  font-size: 0.9rem;
}

/* Graphic Container */
.bucket-graphic {
  position: relative;
  width: 120px;
  height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* The actual "bucket" shape */
.bucket-body {
  width: 100%;
  height: 100%;
  border: 4px solid #ced4da;
  border-top: none; /* Open top */
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
  position: relative;
  overflow: hidden;
  background-color: #f8f9fa;
  box-shadow: inset 0 0 10px rgba(0,0,0,0.05);
}

/* The liquid fill */
.bucket-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  transition: height 2s cubic-bezier(0.25, 1, 0.5, 1);
  opacity: 0.85;
}

/* Optional: Top rim of the bucket */
.bucket-body::before {
  content: '';
  position: absolute;
  top: 0;
  left: -4px;
  right: -4px;
  height: 8px;
  background-color: #ced4da;
  border-radius: 4px;
  z-index: 10;
}

/* --- TABLE SECTION STYLES --- */
.portfolio-buckets {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.bucket-section {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.bucket-header {
  padding: 1rem 1.5rem;
  border-bottom: 2px solid #ced4da;
}

.bucket-header h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.3rem;
}

.bucket-description {
  margin: 0;
  font-size: 0.9rem;
  color: #6c757d;
}

.spend-now h2 { color: #007bff; }
.save-soon h2 { color: #fd7e14; }
.secure-later h2 { color: #28a745; }

.fund-table-container {
  padding: 1rem 1.5rem;
  overflow-x: auto;
}

.loading-state {
  padding: 2rem;
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

.fund-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

/* Table Body Cells - keep comfortable padding */
.fund-table td {
  text-align: left;
  padding: 0.75rem 1rem; 
  border-bottom: 1px solid #e9ecef;
  white-space: nowrap; 
}

/* Table Header Cells - reduce padding to absolute minimum */
.fund-table th {
  background-color: #f1f3f5;
  font-weight: bold;
  color: #495057;
  vertical-align: middle;
  text-align: left; /* Default alignment for main columns */
  padding: 4px 8px; /* DRASTICALLY reduced top/bottom padding */
}

/* Force the "Fund Performance" header to be perfectly centered */
th.performance-group-header {
  text-align: center !important;
  border-bottom: 1px solid #adb5bd !important;
  padding-top: 6px !important;
  padding-bottom: 2px !important; /* Squeezes it closer to the sub-headers */
}

/* Optional: Center the sub-headers (YTD, 1Y, 3Y, 5Y, Max) to look neat under the main header */
.fund-table thead tr:nth-child(2) th {
  text-align: center !important;
}

/* Return Color Classes */

.text-green {
  color: #0f5132; /* A deeper, richer dark green */
  font-weight: 600; /* Made slightly bolder so the positive numbers pop */
}

.text-red {
  color: #dc3545;
  font-weight: 500;
}

.text-muted {
  color: #adb5bd;
}
</style>